"use strict";
import { FEWTOKEN_DEFAULT_ACTIVE_LIST_URLS, RING_DEFAULT_ACTIVE_LIST_URLS } from "constants/lists";
import { useMemo } from "react";
import { useAppSelector } from "state/hooks";
import { getVerifiedFewTokenMappingInfos } from "state/lists/fewTokenMappings";
import { INTERNAL_MAINNET_FEWTOKEN_LP_CANDIDATES } from "state/lists/internalFewTokenLpList";
import { WrappedTokenInfo } from "state/lists/wrappedTokenInfo";
export function useAllLists() {
  return useAppSelector((state) => state.lists.byUrl);
}
function useTokensFromMap(tokenMap, chainId) {
  return useMemo(() => {
    if (!chainId) {
      return {};
    }
    return Object.keys(tokenMap[chainId] ?? {}).reduce((newMap, address) => {
      newMap[address] = tokenMap[chainId][address].token;
      return newMap;
    }, {});
  }, [chainId, tokenMap]);
}
const mapCache = typeof WeakMap !== "undefined" ? /* @__PURE__ */ new WeakMap() : null;
function tokensToChainTokenMap(tokens) {
  const cached = mapCache?.get(tokens);
  if (cached) {
    return cached;
  }
  const [list, infos] = Array.isArray(tokens) ? [void 0, tokens] : [tokens, tokens.tokens];
  const map = infos.reduce((map2, info) => {
    try {
      const token = new WrappedTokenInfo(info, list);
      if (map2[token.chainId]?.[token.address] !== void 0) {
        return map2;
      }
      if (!map2[token.chainId]) {
        map2[token.chainId] = {};
      }
      map2[token.chainId][token.address] = { token, list };
      return map2;
    } catch {
      return map2;
    }
  }, {});
  mapCache?.set(tokens, map);
  return map;
}
function combineMaps(map1, map2) {
  const chainIds = Object.keys(
    Object.keys(map1).concat(Object.keys(map2)).reduce((memo, value) => {
      memo[value] = true;
      return memo;
    }, {})
  ).map((id) => parseInt(id));
  return chainIds.reduce((memo, chainId) => {
    memo[chainId] = {
      ...map2[chainId],
      // map1 takes precedence
      ...map1[chainId]
    };
    return memo;
  }, {});
}
function useCombinedTokenMapFromUrls(urls) {
  const lists = useAllLists();
  return useMemo(() => {
    if (!urls) {
      return {};
    }
    return urls.slice().reduce((allTokens, currentUrl) => {
      const current = lists[currentUrl]?.current;
      if (!current) {
        return allTokens;
      }
      try {
        return combineMaps(allTokens, tokensToChainTokenMap(current));
      } catch (error) {
        return allTokens;
      }
    }, {});
  }, [lists, urls]);
}
function useRingCombinedActiveList() {
  const activeTokens = useCombinedTokenMapFromUrls(RING_DEFAULT_ACTIVE_LIST_URLS);
  return activeTokens;
}
export function useDefaultRingActiveTokens(chainId) {
  const defaultListTokens = useRingCombinedActiveList();
  const tokensFromMap = useTokensFromMap(defaultListTokens, chainId);
  return tokensFromMap;
}
export function useDefaultFewTokenActiveTokensForChainIds(chainIds) {
  return useVerifiedFewTokenMappingsForChainIds(chainIds).map(({ fewToken }) => fewToken);
}
export const FEWTOKEN_MAINTENANCE_ACTIVE_LIST_URLS = Array.from(
  /* @__PURE__ */ new Set([...RING_DEFAULT_ACTIVE_LIST_URLS, ...FEWTOKEN_DEFAULT_ACTIVE_LIST_URLS])
);
function useVerifiedFewTokenMappingsFromListUrls(chainIds, listUrls) {
  const lists = useAllLists();
  return useMemo(() => {
    const seenFewTokens = /* @__PURE__ */ new Set();
    const mappings = [];
    const addMappingInfos = (tokenInfos, list) => {
      for (const mappingInfo of getVerifiedFewTokenMappingInfos(tokenInfos, chainIds)) {
        const key = `${mappingInfo.fewTokenInfo.chainId}:${mappingInfo.fewTokenInfo.address.toLowerCase()}`;
        if (seenFewTokens.has(key)) {
          continue;
        }
        try {
          const originToken = new WrappedTokenInfo(mappingInfo.originTokenInfo, list);
          const fewToken = new WrappedTokenInfo(mappingInfo.fewTokenInfo, list);
          seenFewTokens.add(key);
          mappings.push({ originToken, fewToken });
        } catch {
        }
      }
    };
    addMappingInfos(INTERNAL_MAINNET_FEWTOKEN_LP_CANDIDATES);
    for (const url of listUrls) {
      const current = lists[url]?.current;
      if (current) {
        addMappingInfos(current.tokens, current);
      }
    }
    return mappings;
  }, [chainIds, listUrls, lists]);
}
function useVerifiedFewTokenMappingsForChainIds(chainIds) {
  return useVerifiedFewTokenMappingsFromListUrls(chainIds, FEWTOKEN_DEFAULT_ACTIVE_LIST_URLS);
}
export function useVerifiedFewTokenMaintenanceMappingsForChainIds(chainIds) {
  return useVerifiedFewTokenMappingsFromListUrls(chainIds, FEWTOKEN_MAINTENANCE_ACTIVE_LIST_URLS);
}
